import { yupResolver } from "@hookform/resolvers/yup";
import { Stack, Typography } from "@mui/material";
import Chip from "@mui/material/Chip";
import * as React from "react";
import { HealthAndSafety } from "@mui/icons-material";
import { useContext, useEffect, useMemo } from "react";
import {FormProvider, useForm} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";
import SectionHeader from "../../../../util/component/SectionHeader";
import {InvoiceType, RentalType, VehicleType} from "../../types";
import { dayjsDateTime } from "../../utils";
import { validationSchema } from "./form";
import { businessAgreementCheckValidate, businessAgreementCig, isBusinessContinuityMode, isOfferState, mandatoryDossierIsRequired, rangeDateValidity, stepState } from "./recoil";
import { TableAvailabilities } from "./sections";
import { AvailabilityPrice, CustomerDetails } from "./steps";
import { IFormValues, InsertBookingStep } from "./types";
import DefaultStateManager from "./_default-state-manager/DefaultStateManager";
import StateManager from "./_state-manager/StateManager";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { IRangeDate } from "../../api/services";
import {useQueryClient} from "@tanstack/react-query";

const InsertBookingPage = () => {

    const { t } = useTranslation('booking');

    const step = useRecoilValue(stepState);
    const mandatoryDossierRequired = useRecoilValue<boolean | undefined> (mandatoryDossierIsRequired);
    const hasCig = useRecoilValue<boolean | undefined>(businessAgreementCig);
    const rangeDate = useRecoilValue<IRangeDate | undefined>(rangeDateValidity);
    const bACheckValidate = useRecoilValue<boolean | undefined>(businessAgreementCheckValidate);
    const [businessContinuityMode, setBusinessContinuityMode] = useRecoilState(isBusinessContinuityMode);
    const [isOffer] = useRecoilState(isOfferState);

    const { globalData } = useContext(GlobalContext);
    const queryClient = useQueryClient();

    const form = useForm<IFormValues>({
        defaultValues: {
            basicDetails: {
                rentalType: RentalType.RETAIL,
                pickupLocation: null,
                pickupLocationTimezone: null,
                dropoffLocation: null,
                pickupDate: dayjsDateTime()
                    .add(15, 'm'),
                dropoffDate: dayjsDateTime()
                    .add(3, 'day')
                    .add(15, 'm'),
                vehicleCategory: VehicleType.CAR,
                carGroup: null,
                lockGroup: false,
                mileageIncluded: null,
                customerCode: '',
                dossierNumber: '',
                bookingDateTime: null,
                tenants: globalData?.user?.legalEntities?.length == 1 && globalData?.user?.dealers?.length == 1
                            ? globalData?.user.legalEntities?.at(0)?.name
                            : null,
                dealers: globalData?.user?.legalEntities?.length == 1 && globalData?.user?.dealers?.length == 1
                            ? globalData?.user.dealers?.at(0)?.name
                            : null,
            },
            extraServices: {
                ancillary: {},
                discountOverprice: null,
                note: '',
                affiliationCode: '',
                flightNumber: '',
                discountType: null,
                checkAffiliated: null,
                checkCoupon: null
            },
            firstDriver: {
                id: "",
                name: "",
                surname: "",
                birthDate: null,
                birthPlace: "",
                birthNation: "",
                residenceCountry: "",
                fiscalCode: "",
                drivingLicenseNumber: "",
                email: "",
                mobile: "",
                uuid: "",
                mobilePrefix: null,
                gender: ''
            },
            invoice: {
                id: "",
                type: InvoiceType.PERSON,
                name: "",
                surname: "",
                birthDate: null,
                birthNation: "",
                birthPlace: "",
                residenceCountry: "",
                fiscalCode: "",
                drivingLicenseNumber: "",
                email: "",
                mobile: "",
                billingCountry: null,
                billingCity: "",
                billingStreetName: "",
                billingStreetNumber: "",
                billingPostalCode: "",
                billingStreetNameGoogleSearch: "",
                certifiedEmail: "",
                vatNumber: "",
                vatNumberCountry: "",
                invoiceCode: "",
                companyName: "",
                uuid: "",
                mobilePrefix: null,
                billingProvince: null,
                linkedToBusinessAgreement: false,
                legalEntity: null,
                publicAuthority: false,
                paIdNumber: ''
            },
            bookingOwner: {
                id: "",
                name: "",
                surname: "",
                birthDate: null,
                birthNation: "",
                birthPlace: "",
                residenceCountry: "",
                fiscalCode: "",
                drivingLicenseNumber: "",
                email: "",
                mobile: "",
                companyName: "",
                country: "",
                city: "",
                streetName: "",
                streetNumber: "",
                postalCode: "",
                certifiedEmail: "",
                telephone: "",
                invoiceCode: "",
                vatNumber: "",
                vatNumberCountry: "",
                mobilePrefix: null,
                linkedToBusinessAgreement: false,
                legalEntity: null,
                publicAuthority: false,
                paIdNumber: ''
            },
            additionalDrivers: [],
            forceBookingOwner: true
        },
        reValidateMode: 'onSubmit',
        resolver: yupResolver(validationSchema, {}, { rawValues: true }),
        context: {
            step,
            mandatoryDossierRequired,
            hasCig,
            rangeDate,
            bACheckValidate,
            dmsAssociatedOperator: globalData.user.mobilityStores.map(
                ((elem:any) => {
                    return elem.sapId;
                })
            ),
            businessContinuityMode,
            isOffer
        }
    });

    useEffect(() => {
        setBusinessContinuityMode(window.location.pathname.includes('insert-booking'));
    }, [window.location]);

    useEffect(() => {
        if(businessContinuityMode) {
            form.resetField("basicDetails.pickupDate", {defaultValue: dayjsDateTime()});
            form.resetField("basicDetails.dropoffDate", {defaultValue: dayjsDateTime().add(3, 'day')});
        }

    }, [businessContinuityMode]);

    const title = useMemo<string>(() => (
        businessContinuityMode ? t('label.availabilityAndPriceRecovery') : (
        step === InsertBookingStep.AVAILABILITY_PRICE ? t('label.availabilityAndPrice') : t('label.customerDetails'))
    ), [step, businessContinuityMode]);

    const insertTemplate = useMemo(() => {
        if(businessContinuityMode!== undefined) {
            return (
                <>
                    <SectionHeader title={
                        <Stack direction="row" alignItems="center" columnGap={3}>
                            <Stack direction="row" alignItems="end" columnGap={1}>
                                <Typography variant="bodyLarge">{title}</Typography>
                                {businessContinuityMode &&
                                    <Chip label={t(`label.recovery-mode`)} color={'error'} icon={<HealthAndSafety />}/>
                                }
                            </Stack>
                        </Stack>
                    } />
                    <FormProvider {...form}>
                        <Stack rowGap={2} p={3}>
                            {step === InsertBookingStep.AVAILABILITY_PRICE && <AvailabilityPrice />}
                            {step === InsertBookingStep.CUSTOMER_DETAILS && <CustomerDetails />}
                            <TableAvailabilities />
                        </Stack>
                        <StateManager />
                        <DefaultStateManager />
                    </FormProvider>
                </>
            );
        }
        return (
            <></>
        )
    }, [businessContinuityMode, step]);

    return (
        insertTemplate
    );
}

export default InsertBookingPage;
